/*
 * URL shape for /history.
 *
 *   /history                                  default view
 *   /history/season/2025-26/player-stats      a season tab
 *   /history/season/2025-26                   that season, default tab
 *   /history/player-archive                   spans every season
 *   /history/top-seasons                      spans every season
 *
 * Everything is path segments, no query string. Each tab therefore reports
 * as its own page path in analytics, where the whole page previously
 * counted as a single /history hit. Season is in the path too, so a link
 * pins both the tab and the season — the cost being that per-tab totals
 * across seasons need a path filter to aggregate.
 */

export const SEASON_TAB_SLUGS = [
	'player-stats',
	'team-stats',
	'gw-matrix',
	'fixture-ticker',
	'double-digit-hauls',
	'hauls-by-player',
	'hauls-by-fixture',
	'bar-chart-race',
];

export const PRIMARY = {
	SEASON: 'season-archive',
	PLAYER: 'player-archive',
	TOP: 'top-seasons',
};

export const DEFAULT_SEASON_TAB = 'player-stats';

/*
 * Parse the part of the path after /history/.
 * Returns { primary, season, seasonTab }; season is null when the URL does
 * not pin one, and the caller falls back to the latest.
 */
export function parseHistoryPath(rest = '') {
	const parts = rest.split('/').filter(Boolean);

	if (parts[0] === PRIMARY.PLAYER) return { primary: PRIMARY.PLAYER, season: null, seasonTab: null };
	if (parts[0] === PRIMARY.TOP) return { primary: PRIMARY.TOP, season: null, seasonTab: null };

	if (parts[0] === 'season') {
		const season = parts[1] ?? null;
		const slug = parts[2];
		return {
			primary: PRIMARY.SEASON,
			season,
			seasonTab: SEASON_TAB_SLUGS.includes(slug) ? slug : DEFAULT_SEASON_TAB,
		};
	}

	/* Bare /history, or anything unrecognised, lands on the default rather
	 * than a dead page. */
	return { primary: PRIMARY.SEASON, season: null, seasonTab: DEFAULT_SEASON_TAB };
}

export function historyPath({ primary, season, seasonTab }) {
	if (primary === PRIMARY.PLAYER) return '/history/player-archive';
	if (primary === PRIMARY.TOP) return '/history/top-seasons';
	const tab = seasonTab ?? DEFAULT_SEASON_TAB;
	/* Without a known season yet, stay on the bare path rather than writing
	 * a guess into the URL. */
	return season ? `/history/season/${season}/${tab}` : `/history/${tab === DEFAULT_SEASON_TAB ? '' : tab}`.replace(/\/$/, '');
}
